import { Dropdown } from 'react-bootstrap';
import { I18n } from '@/i18n/I18n';
import { INSTRUMENT_CATEGORY } from '@/types/equipmentTypes';
import { DESKTOP_INSTRUMENT_TYPE, DesktopInstrument } from '@/types/instrumentBridgeTypes';
import { categoryDropdownString } from './InstrumentCategoryDropdown';

/**
 * Choosing which instrument an equipment item uses.
 *
 * Ported from veriform-desktop's `ConnectionModal` (Equipment/), down to the
 * badges and the colour swatch, so an instrument reads the same in both apps.
 * Two things are left behind: the `+ Instrument` button, which installs a
 * provider on the machine and is not something a page can do, and desktop's
 * raw `data-bs-toggle` -- this uses react-bootstrap's own `Dropdown`.
 *
 * Selecting one opens the connect dialog, as it does there. Nothing is recorded
 * until that dialog is confirmed.
 */

/** ConnectionModal.tsx:12. Manual control reads better than its raw type. */
function typeBadgeLabel(instrumentType: string): string {
  return instrumentType === DESKTOP_INSTRUMENT_TYPE.MANUAL_CONTROL ? 'Manual' : instrumentType;
}

/**
 * ConnectionModal.tsx:104. Nothing for an instrument without a category, and
 * nothing for a camera, whose category only ever repeats what it already is.
 */
function showCategoryBadge(instrument: DesktopInstrument): boolean {
  if (!instrument.category) return false;
  return !(instrument.instrumentType === DESKTOP_INSTRUMENT_TYPE.CAMERA
    && instrument.category === 'camera');
}

function Swatch(props: { color?: string }) {
  return (
    <div
      aria-hidden="true"
      style={{
        backgroundColor: props.color || 'grey',
        width: '15px',
        height: '15px',
        minWidth: '15px',
        minHeight: '15px',
      }}
    />
  );
}

export function InstrumentPicker(props: {
  instruments: DesktopInstrument[];
  selected?: DesktopInstrument;
  disabled?: boolean;
  onSelect: (instrument: DesktopInstrument) => void;
}) {
  const { instruments, selected, disabled, onSelect } = props;

  return (
    <Dropdown className="w-100">
      <Dropdown.Toggle
        variant="light"
        disabled={disabled}
        aria-label={I18n.get('selectInstrument')}
        className="w-100 text-start d-flex align-items-center justify-content-between border"
        style={{ backgroundColor: selected ? 'white' : undefined }}
      >
        <div className="d-flex gap-2 align-items-center">
          <Swatch color={selected?.color} />
          { selected?.instrumentName ?? I18n.get('selectInstrument') }
        </div>
      </Dropdown.Toggle>

      <Dropdown.Menu className="overflow-auto" style={{ maxHeight: '50vh' }}>
        { instruments.map(instrument => (
          <Dropdown.Item
            key={instrument.id}
            as="button"
            type="button"
            aria-label={instrument.instrumentName}
            onClick={() => onSelect(instrument)}
          >
            <div className="d-flex gap-1 align-items-center">
              <div className="badge bg-secondary my-auto">
                { typeBadgeLabel(instrument.instrumentType) }
              </div>
              { showCategoryBadge(instrument) && (
                <div className="badge bg-secondary my-auto">
                  { categoryDropdownString(instrument.category as INSTRUMENT_CATEGORY) }
                </div>
              )}
              <Swatch color={instrument.color} />
              { instrument.instrumentName }
            </div>
          </Dropdown.Item>
        )) }
      </Dropdown.Menu>
    </Dropdown>
  );
}
